import React, { useState, useEffect } from "react";
import {
  Grid,
  Typography,
  Card,
  CardContent,
  Box,
  Button,
} from "@mui/material";
import { styled, keyframes } from "@mui/system";
import exams from "../assets/exams.png";
import sslc from "../assets/class.png";
import pg from "../assets/pg.png";
import ug from "../assets/ug.png";
import puc from "../assets/puc.png";
import engineering from "../assets/engineering.png";
import logo from "../assets/mobile.png";
import banner1 from "../assets/banner1.png";
import banner2 from "../assets/banner2.png";

import studyFlow from "../assets/image1.png";
import examFocus from "../assets/image2.png";
import Header from "./Header";
import Footer from "./Footer";
import { useNavigate } from "react-router-dom";

/* Card Style */
const StyledCard = styled(Card)({
  height: "100%",
  borderRadius: 16,
  boxShadow: "0px 10px 20px rgba(0,0,0,0.15)",
  transition: "0.3s",
  "&:hover": {
    transform: "translateY(-8px)",
    boxShadow: "0px 18px 28px rgba(0,0,0,0.3)",
  },
});


/* Simple animations */
const fadeUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(25px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(40px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const slideRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(-40px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const sections = [
  {
    title: "SSLC",
    description:
      "For anyone tackling SSLC, grab full guides, clear lessons, and solid practice kits. Access all the content you need to prepare effectively and excel in your exams.",
    image: sslc,
  },
  {
    title: "PUC",
    description:
      "You’ll find every vital note, useful resource, and exam guide contributed by PUC students. Access everything you need to excel in your exams, tailored to your curriculum and subjects.",
    image: puc,
  },
  {
    title: "Undergraduate",
    description:
      "Get detailed lecture notes, practice sheets, and extra resources for any college course you take. Get all the tools you need to ace your tests and raise your grades.",
    image: ug,
  },
  {
    title: "Postgraduate",
    description:
      "Access comprehensive Educational notes, reference materials, and test resources designed to meet all your PG academic needs.",
    image: pg,
  },
  {
    title: "Engineering",
    description:
      "Your portal gives you quick access to lecture slides, flashcards and rubrics that match every class you take.",
    image: engineering,
  },
  {
    title: "Exam Notes & Syllabus",
    description:
      "Comprehensive exam summaries and syllabus packs are available, built around the individual needs of every student. Get all your study guides, review them well, and boost your exam scores.",
    image: exams,
  },
];

const whyChooseData = [
  {
    title: "100% Free Access",
    description:
      "All study materials, syllabus, and exam notes are available completely free for students.",
  },
  {
    title: "Exam-Oriented Content",
    description:
      "Notes and resources are designed strictly based on syllabus and exam patterns.",
  },
  {
    title: "Regular Updates",
    description:
      "We continuously update content to match the latest academic changes.",
  },
  {
    title: "Simple & Student Friendly",
    description:
      "Easy navigation and well-organized subjects for stress-free learning.",
  },
];

const howItWorksSteps = [
  {
    step: "1",
    title: "Choose Your Level",
    description:
      "Pick SSLC, PUC, UG, PG, or Engineering so you only see content that matches your journey.",
  },
  {
    step: "2",
    title: "Browse Subjects",
    description:
      "Open the subjects and topics you need and quickly find notes, syllabus, and questions.",
  },
  {
    step: "3",
    title: "Study & Revise",
    description:
      "Use organized notes and exam-focused material to revise smarter and boost your scores.",
  },
];

const HomePage = () => {
  const navigate = useNavigate();
  const banners = [banner1, banner2, banner1];
  const [index, setIndex] = useState(0);
  const [nextIndex, setNextIndex] = useState(1);
  const [showNext, setShowNext] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setShowNext(true); // start cross-fade

      setTimeout(() => {
        setIndex(nextIndex);
        setNextIndex((prev) =>
          prev === banners.length - 1 ? 0 : prev + 1
        );
        setShowNext(false);
      }, 200); // fade duration

    }, 3000); // total time per image

    return () => clearInterval(interval);
  }, [nextIndex, banners.length]);

  return (
    <>
      <Header />
      <Grid container>
        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: { xs: 280, md: 500 },
            overflow: "hidden",
            backgroundColor: "transparent",
          }}
        >
          {/* Current Image */}
          <Box
            component="img"
            src={banners[index]}
            sx={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              transition: "opacity 1.0s ease-in-out",
              opacity: showNext ? 0 : 1,
            }}
          />

          {/* Next Image */}
          <Box
            component="img"
            src={banners[nextIndex]}
            sx={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              transition: "opacity 1.5s ease-in-out",
              opacity: showNext ? 1 : 0,
            }}
          />
        </Box>
      </Grid>


      <Grid
        container
        spacing={4}
        sx={{
          px: { xs: 2, md: 10 },
          py: { xs: 6, md: 10 },
          background: "#ffffff",
        }}
      >
        <Grid size={{ xs: 12, md: 12 }}>
          <Typography
            sx={{
              fontSize: { xs: 25, md: 40 },
              color: '#1A4B83',
              fontWeight: "bold",
              textAlign: "center",
              mx: 'auto',
              mb: 3,
              borderBottom: "3px solid #1A4B83",

              width: "fit-content",
            }}
          >
            Why Choose GetMyRank?
          </Typography>

          <Typography
            sx={{
              textAlign: "center",
              mt: 1,
              fontSize: { xs: 14, md: 18 },
              color: "text.secondary",
            }}
          >
            Everything you need to prepare better, score higher, and stay
            confident.
          </Typography>
        </Grid>

        {whyChooseData.map((item, index) => (
          <Grid size={{ xs: 12, md: 3 }} key={index}>
            <Card
              sx={{
                height: "100%",
                borderRadius: 3,
                textAlign: "center",
                boxShadow: 6,
                transition: "0.3s",
                "&:hover": {
                  transform: "translateY(-6px)",
                },
              }}
            >
              <CardContent
                sx={{ alignItems: "center", justifyContent: "center" }}
              >
                <Typography variant="h6" fontWeight="bold">
                  {item.title}
                </Typography>

                <Typography mt={1} color="text.secondary">
                  {item.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Main Sections */}
      <Grid
        container
        spacing={4}
        sx={{
          p: { xs: 2, md: 10 },
          background: "linear-gradient(60deg,#F9FAFF, #F3F4FF)",
        }}
      >
        <Grid size={{ xs: 12, md: 12 }}>
          <Box>

            <Typography
              sx={{
                fontSize: { xs: 25, md: 40 },
                color: '#1A4B83',
                fontWeight: "bold",
                textAlign: "center",
                mx: 'auto',
                mb: 3,
                borderBottom: "3px solid #1A4B83",

                width: "fit-content",
              }}
            >
              Welcome to GetMyRank
            </Typography>

            <Typography
              sx={{
                textAlign: "center",
                mt: 1,
                fontSize: { xs: 15, md: 19 },
              }}
            >
              Your one-stop platform for SSLC, PUC, UG, and PG notes, study
              materials, and exam resources.
            </Typography>
          </Box>
        </Grid>

        {sections.map((s, i) => (
          <Grid size={{ xs: 12, md: 4, sm: 4 }} key={i}>
            <StyledCard
              sx={{
                borderLeft: "4px solid #1A4B83",
                borderRight: "4px solid #1A4B83",
              }}
            >
              <CardContent sx={{ textAlign: "center" }}>
                <Box component="img" src={s.image} sx={{ width: 90, mb: 2 }} />

                <Typography variant="h5" fontWeight="bold">
                  {s.title}
                </Typography>

                <Typography mt={1}>{s.description}</Typography>

                <Button
                  onClick={() => navigate("/categories")}
                  variant="contained"
                  sx={{
                    mt: 2,
                    background:
                      "linear-gradient(60deg,#0D2A54, #1A4B83, #2B6BA5)",
                    fontWeight: "bold",
                  }}
                >
                  Read More
                </Button>
              </CardContent>
            </StyledCard>
          </Grid>
        ))}
      </Grid>

      {/* Floating Logo */}
      <Box
        sx={{
          position: "fixed",
          right: 0,
          bottom: "5%",
          bgcolor: "#fff",
          p: 1,
          boxShadow: 20,
          borderRadius: "50px 0 0 50px",
        }}
      >
        <Box component="img" src={logo} sx={{ width: 60 }} />
      </Box>

      <Box
        sx={{
          background: "#ffffff",
          px: { xs: 2, md: 10 },
          py: { xs: 6, md: 8 },
        }}
      >
        <Grid container spacing={4} alignItems="center">
          <Grid size={{ xs: 12, md: 6 }}>
            <Box
              sx={{
                opacity: 0,
                animation: `${fadeUp} 0.7s ease-out forwards`,
                animationDelay: "0.15s",
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: 25, md: 40 },
                  color: '#1A4B83',
                  fontWeight: "bold",
                  textAlign: "left",
                  // mx: 'auto',
                  mb: 3,
                  borderBottom: "3px solid #1A4B83",

                  width: "fit-content",
                }}
              >
                Built For Every Student
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: 14, md: 16 },
                  color: "text.secondary",
                  mb: 2,
                }}
              >
                Whether you are in SSLC, PUC, UG, PG, or Engineering, GetMyRank
                keeps all your key notes and syllabus in one clean place.
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: 14, md: 16 },
                  color: "text.secondary",
                }}
              >
                Jump between subjects, track what you finished, and quickly open
                the exact material you need before class or exams.
              </Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Box
              sx={{
                borderRadius: 4,
                overflow: "hidden",
                boxShadow: 5,
                opacity: 0,
                animation: `${slideRight} 0.7s ease-out forwards`,
              }}
            >
              <Box
                component="img"
                src={studyFlow}
                alt="Study flow"
                sx={{
                  width: "105%",
                  height: { xs: 220, md: 350 },
                  objectFit: "cover",
                }}
              />
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Box
        sx={{
          background: "linear-gradient(60deg,#F9FAFF,#F3F4FF)",
          px: { xs: 2, md: 10 },
          py: { xs: 6, md: 8 },
        }}
      >
        <Grid
          container
          spacing={4}
          alignItems="center"
          sx={{ flexDirection: { xs: "column", md: "row-reverse" } }}
        >
          <Grid size={{ xs: 12, md: 6 }}>
            <Box
              sx={{
                opacity: 0,
                animation: `${fadeUp} 0.7s ease-out forwards`,
                animationDelay: "0.15s",
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: 25, md: 40 },
                  color: '#1A4B83',
                  fontWeight: "bold",
                  textAlign: "left",
                  // mx: 'auto',
                  mb: 3,
                  borderBottom: "3px solid #1A4B83",

                  width: "fit-content",
                }}
              >
                Stay Ahead Of Exams
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: 14, md: 16 },
                  color: "text.secondary",
                  mb: 2,
                }}
              >
                Go through chapter-wise notes, model questions, and syllabus
                summaries designed around actual exam patterns.
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: 14, md: 16 },
                  color: "text.secondary",
                }}
              >
                Use GetMyRank daily for quick revision sessions so you never
                feel lost a week before your exams again.
              </Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Box
              sx={{
                borderRadius: 5,
                overflow: "hidden",
                // boxShadow: 8,
                opacity: 0,
                animation: `${slideLeft} 0.7s ease-out forwards`,
              }}
            >
              <Box
                component="img"
                src={examFocus}
                alt="Exam focus"
                sx={{
                  width: "100%",
                  height: { xs: 220, md: 400 },
                  objectFit: "cover",
                }}
              />
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* How It Works */}
      <Box
        sx={{
          background: "linear-gradient(180deg,#F3F4FF,#ffffff)",
          px: { xs: 2, md: 10 },
          py: { xs: 6, md: 8 },
        }}
      >
        <Typography
          sx={{
            fontSize: { xs: 25, md: 40 },
            color: '#1A4B83',
            fontWeight: "bold",
            textAlign: "center",
            mx: 'auto',
            mb: 3,
            borderBottom: "3px solid #1A4B83",

            width: "fit-content",
          }}
        >
          How GetMyRank Works
        </Typography>
        <Typography
          sx={{
            textAlign: "center",
            fontSize: { xs: 14, md: 18 },
            color: "text.secondary",
            maxWidth: 700,
            mx: "auto",
            mb: { xs: 4, md: 5 },
          }}
        >
          Start from your class or course, explore subjects, and move through
          clear notes and exam-focused resources step by step.
        </Typography>

        <Grid container spacing={4}>
          {howItWorksSteps.map((step, i) => (
            <Grid size={{ xs: 12, md: 4 }} key={i}>
              <Card
                sx={{
                  height: "100%",
                  borderRadius: 4,
                  boxShadow: 6,
                  p: 3,
                  textAlign: "left",
                  position: "relative",
                  overflow: "hidden",
                  opacity: 0,
                  animation: `${fadeUp} 0.7s ease-out forwards`,
                  animationDelay: `${0.15 * i}s`,
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: -20,
                    right: -5,
                    fontSize: 80,
                    fontWeight: "bold",
                    color: "#ccc",
                  }}
                >
                  {step.step}
                </Box>
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  sx={{ mb: 1.5, color: "#0D2A54" }}
                >
                  {step.title}
                </Typography>
                <Typography color="text.secondary">
                  {step.description}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      <Footer />
    </>
  );
};

export default HomePage;

